<template>
  <div class="add-marks">
    <ui-button
      ref="menuButton"
      :disabled="isDisabled"
      :menu-props="{
        location: 'bottom start',
        offset: [10, 12],
      }"
      icon="fas fa-flag"
      :planLimits="planLimits"
    >
      <template #menu>
        <div class="menu-mark">
          <div class="mark-container">
            <span
              :class="{ 'add-marks__icon-wrapper--disabled':!hasSelectedItem }"
              class="mark-item"
              v-for="(item, index) in items"
              :key="index"
              :disabled="!hasSelectedItem"
              @click="handleMarkClick(item.mark)"
            >
              <ui-icon
                class="cursor-pointer"
                :color="$colors[item.color]"
                :icon="item.icon"
              ></ui-icon>
            </span>
          </div>

          <span
            :class="{ 'add-marks__delete--disabled': !hasSelectedItem }"
            class="add-marks__delete p-4"
            @click.stop="handleMarkClick(null)"
          >{{ t('Delete marks') }}</span>
        </div>
      </template>
    </ui-button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  useColors,
  useI18n,
  useMainMenuComplexTabs,
  useTableQuery,
  useUrl,
} from '@/composables';
import { $emitter } from '@/services/emitter';
import api, { type AddMarksApi } from './api';
import markList from './mark-list';
import type UiButton from '@/components/ui/ui-button.vue';

const { t } = useI18n();
const $colors = useColors();
const route = useRoute();
const tableQueryApi = useTableQuery();
const { isReport, campaignAsFirstTableFilterCondition } = useMainMenuComplexTabs();
const { isMainTab } = useUrl();

const marksApi: AddMarksApi = api;
const items = markList;
const menuButton = ref<InstanceType<typeof UiButton> | null>(null);

const planLimitsType = (type: string) => {
  switch (type) {
    case 'campaigns':
      return 'markersCampaigns';
    case 'offers':
      return 'markersOffers';
    case 'landings':
      return 'markersLandings';
    case 'flows':
      return 'markersFlows';
    case 'affiliate-networks':
      return 'markersAffiliateNetworks';
    case 'traffic-sources':
      return 'markersTrafficSources';
  }
};

const planLimits = computed((): string | boolean => {
  if (route.params?.type?.includes('custom')) return 'markersCustomParams';

  if (
    tableQueryApi.tableQueryTemporaryForCurrentTab.value.view === 'tree'
    && tableQueryApi.tableSelectedItems.value.length === 1
  ) {
    const path = (tableQueryApi.tableSelectedItems.value[0] as { grouping?: string })?.grouping
      .replace('-name', '')
      .replace('_', '-');
    return planLimitsType(path);
  }

  return planLimitsType(route.params.type as string);
});

const filteredSelectedItems = computed(() =>
  tableQueryApi.tableSelectedItems.value
    ? (tableQueryApi.tableSelectedItems.value as Array<{ DT_RowId: string; grouping?: string }>).filter(
        ({ DT_RowId }) => DT_RowId !== 'Unknown',
      )
    : [],
);

const customValues = computed((): string[] =>
  filteredSelectedItems.value
    .map((item: { DT_RowId: string }) => item.DT_RowId)
    .filter(({ isDirect }: { isDirect?: boolean }) => !isDirect),
);

const hasSelectedItem = computed(() => {
  const { grouping } = filteredSelectedItems.value.length === 1 && filteredSelectedItems.value[0];

  if (grouping) {
    const path = grouping.replace('-name', '').replace('_', '-');

    return ['campaigns', 'offers', 'landings', 'flows', 'traffic-sources', 'affiliate-networks'].includes(path)
      || path.includes('reports-custom');
  }

  return !!customValues.value.length;
});

const tableDrilldownValues = computed(() =>
  tableQueryApi.tableDrilldownFilter.value.reduce((accumulator, { filterValues }) => {
    accumulator = accumulator.concat(filterValues.split('^'));
    return accumulator;
  }, [] as string[]),
);

const isDisabled = computed(() =>
  !hasSelectedItem.value
  || (
    isReport.value
    && !campaignAsFirstTableFilterCondition.value
    && route.params?.type?.includes('custom')
  ),
);

const addCustomMark = (mark: number | null, type = route.params.type as string) => {
  const form: Bemob.CustomMark = {
    mark,
    customParameter: type,
    customValues: customValues.value,
    ids: tableDrilldownValues.value,
  };

  marksApi.put(form).then(() => {
    $emitter.emit('marks-added', form);
  });
};

const addBasicMark = (mark: number | null, path = route.params.type as string) => {
  const form: Bemob.CustomMarkBase = {
    mark,
    ids: customValues.value,
  };

  marksApi.putBasic(path, form).then(() => {
    $emitter.emit('marks-added', form);
  });
};

const handleMarkClick = (mark?: number | null) => {
  if (!hasSelectedItem.value) return;

  menuButton.value?.toggleMenu();

  if (filteredSelectedItems.value[0]?.grouping?.startsWith('reports-custom')) {
    addCustomMark(mark, filteredSelectedItems.value[0].grouping.replace('reports-', ''));
    return;
  }

  const { grouping } = filteredSelectedItems.value.length === 1 && filteredSelectedItems.value[0];

  if (grouping) {
    const path = grouping.replace('-name', '').replace('_', '-');
    addBasicMark(mark, path);
  } else if (isMainTab.value) {
    addBasicMark(mark);
  }
};
</script>

<style lang="scss" scoped>
.menu-wrapper {
  z-index: 10;
}
  .menu-mark {
    background-color: var(--color-white);
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;


    .mark-container {
      display: flex;
      flex-wrap: wrap;
      padding: 8px;
    }

    .mark-item {
      justify-content: center;
      display: flex;
      width: 25%;
      height: 25px;
      align-items: center;
    }
  }

  .disabled {
    pointer-events: none;
    color: var(--color-gray5);
  }
</style>

<style lang="scss">
  .add-marks{
    display: flex;
    align-items: center;
  }

  .line-decor{
    width: 2px;
    height: 14px;
    background-color: var(--color-gray20);
    margin-right: 8px;
  }

  .add-marks__icon-wrapper--disabled{
    opacity: 0.5;
  }

  .add-marks__icon {
    margin-bottom: 2px;
    margin-right: 8px;

    &:hover{
      cursor: pointer;
    }
  }

  .add-marks__delete{
    font-size: 14px;
    line-height: 16px;
    color: var(--color-red)!important;
    transition: all .2s;

    display: flex;
    align-items: center;
    justify-content: center;
    border-top: 1px solid var(--color-gray10);
    padding: 8px;
    width: 100%;

    &:hover{
      cursor: pointer;
      color: var(--color-red);
    }
  }
  .add-marks__delete--disabled{
    opacity: 0.5;
  }
</style>
